import { persistOperationalChat, taskPresentation } from './operationalChat';

const normalize = value => (value || '').toLocaleLowerCase('pt-BR').normalize('NFD').replace(/[\u0300-\u036f]/g, '');

const TASK_INTENT = /\b(?:crie|criar|adicione|adicionar|registre|registrar|anote|anotar|coloque|colocar)\s+(?:uma\s+)?tarefa\b/i;

const WEEKDAYS = { domingo: 0, segunda: 1, terca: 2, quarta: 3, quinta: 4, sexta: 5, sabado: 6 };

function nextWeekday(date, weekday, hour, minute) {
  const candidate = new Date(date);
  candidate.setDate(candidate.getDate() + (weekday - candidate.getDay() + 7) % 7);
  candidate.setHours(hour, minute, 0, 0);
  if (candidate.getTime() <= Date.now()) candidate.setDate(candidate.getDate() + 7);
  return candidate;
}

function parseDueDate(text) {
  const now = new Date();
  const time = text.match(/\b(?:as|a)\s*(\d{1,2})(?:\s*h\s*(\d{2})?|:(\d{2}))?\b/);
  const hour = time ? Number(time[1]) : 18;
  const minute = time ? Number(time[2] || time[3] || 0) : 0;
  if (hour > 23 || minute > 59) return { invalid: true };
  const due = new Date(now);
  let hasDate = false;
  if (/\bamanha\b/.test(text)) { due.setDate(due.getDate() + 1); hasDate = true; }
  else if (/\bhoje\b/.test(text)) hasDate = true;
  else {
    const numeric = text.match(/\b(?:ate|para|no dia)?\s*(\d{1,2})\/(\d{1,2})(?:\/(\d{2,4}))?\b/);
    if (numeric) {
      const day = Number(numeric[1]); const month = Number(numeric[2]) - 1;
      const year = numeric[3] ? Number(numeric[3].length === 2 ? `20${numeric[3]}` : numeric[3]) : due.getFullYear();
      due.setFullYear(year, month, day);
      if (due.getFullYear() !== year || due.getMonth() !== month || due.getDate() !== day) return { invalid: true };
      due.setHours(hour, minute, 0, 0);
      if (!numeric[3] && due.getTime() <= Date.now()) due.setFullYear(year + 1);
      hasDate = true;
    } else {
      const weekday = Object.entries(WEEKDAYS).find(([name]) => new RegExp(`\\b${name}(?:-feira)?\\b`).test(text));
      if (weekday) return { dueAt: nextWeekday(now, weekday[1], hour, minute).toISOString() };
    }
  }
  if (!hasDate) return { dueAt: null };
  due.setHours(hour, minute, 0, 0);
  if (due.getTime() <= Date.now()) return { past: true };
  return { dueAt: due.toISOString() };
}

export function parseTaskChatRequest(message) {
  const raw = (message || '').trim();
  const text = normalize(raw);
  if (!TASK_INTENT.test(text)) return null;
  const parsedDue = parseDueDate(text);
  if (parsedDue.invalid) return { incomplete: true, message: 'Não entendi esse prazo. Pode informar uma data e horário válidos?' };
  if (parsedDue.past) return { incomplete: true, message: 'Esse prazo já passou. Qual data futura você quer para a tarefa?' };
  const title = raw
    .replace(/^(?:por favor,?\s*)?(?:crie|criar|adicione|adicionar|registre|registrar|anote|anotar|coloque|colocar)\s+(?:uma\s+)?tarefa\s*(?:de|para)?\s*/i, '')
    .replace(/(?:hoje|amanhã|amanha)/ig, '')
    .replace(/\b(?:para|na|no)\s+(?:domingo|segunda(?:-feira)?|terça(?:-feira)?|terca(?:-feira)?|quarta(?:-feira)?|quinta(?:-feira)?|sexta(?:-feira)?|sábado|sabado)/ig, '')
    .replace(/(?:domingo|segunda(?:-feira)?|terça(?:-feira)?|terca(?:-feira)?|quarta(?:-feira)?|quinta(?:-feira)?|sexta(?:-feira)?|sábado|sabado)/ig, '')
    .replace(/\b(?:até|ate|para|no dia)\s*\d{1,2}\/\d{1,2}(?:\/\d{2,4})?\b/ig, '')
    .replace(/\b\d{1,2}\/\d{1,2}(?:\/\d{2,4})?\b/g, '')
    .replace(/(?:às|as)\s*\d{1,2}(?:\s*h\s*\d{0,2}|:\d{2})?/ig, '')
    .replace(/\b(?:urgente|prioridade\s+(?:alta|normal|baixa))\b/ig, '')
    .replace(/\s+/g, ' ')
    .replace(/^[,;:\-–—\s]+|[,;:\-–—\s]+$/g, '');
  if (title.length < 2) return { incomplete: true, message: 'Qual tarefa você quer que eu registre?' };
  const priority = /\b(?:urgente|prioridade\s+alta)\b/i.test(raw) ? 'high' : /\bprioridade\s+baixa\b/i.test(raw) ? 'low' : 'normal';
  return { title: title.slice(0, 160), due_at: parsedDue.dueAt, priority };
}

export function reminderMinutesForTask(dueAt) {
  const timestamp = Date.parse(dueAt || '');
  return Number.isFinite(timestamp) && timestamp - Date.now() > 24 * 60 * 60 * 1000 ? [1440, 15] : [15];
}

export async function createTaskFromChat({ apiBase, token, request, projectId = null, sessionId = null, userMessage = '' }) {
  const response = await fetch(`${apiBase}/tasks`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({
      title: request.title,
      due_at: request.due_at,
      priority: request.priority,
      project_id: projectId,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Fortaleza',
      reminder_minutes: request.due_at ? reminderMinutesForTask(request.due_at) : [],
    }),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.detail || 'Não consegui registrar a tarefa');
  const when = request.due_at
    ? ` para ${new Intl.DateTimeFormat('pt-BR', { dateStyle: 'full', timeStyle: 'short' }).format(new Date(request.due_at))}`
    : '';
  const scheduled = Number(data.reminders_scheduled || 0);
  const reminder = request.due_at
    ? scheduled === 0 ? ' Como esse prazo está muito próximo, não há tempo hábil para criar um novo lembrete.'
      : reminderMinutesForTask(request.due_at).includes(1440) ? ' Vou avisar no dia anterior e 15 minutos antes.' : ' Vou avisar 15 minutos antes.'
    : '';
  const answer = `Pronto. Registrei a tarefa “${request.title}”${when}.${reminder}`;
  const priorityLabel = request.priority === 'high' ? 'Prioridade alta' : request.priority === 'low' ? 'Prioridade baixa' : 'Prioridade normal';
  const responsePresentation = taskPresentation({
    title: request.title,
    summary: request.due_at ? `Prazo definido${when}.` : 'Tarefa registrada sem prazo.',
    when: request.due_at || undefined,
    priority: priorityLabel,
    items: reminder ? [reminder.trim()] : [],
  });
  const persisted = await persistOperationalChat({
    apiBase, token, sessionId, userMessage, assistantMessage: answer, projectId, responsePresentation,
  }).catch(() => null);
  return {
    answer,
    sessionId: persisted?.sessionId || null,
    responseMessageId: persisted?.responseMessageId || null,
    responsePresentation: persisted?.responsePresentation || responsePresentation,
  };
}
